import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import baseTheme from "@insite/mobius/globals/baseTheme";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HorizontalRule } from "@ucs/Components/HorizontalRule";
import { formatDate } from "@ucs/Helpers/Formatting";
import Checkpoint from "@ucs/Icons/Checkpoint";
import ChevronRight from "@ucs/Icons/ChevronRight";
import ChevronSmallRight from "@ucs/Icons/ChevronSmallRight";
import SettingsWheel from "@ucs/Icons/SettingsWheel";
import Truck from "@ucs/Icons/Truck";
import UCSColors from "@ucs/Utils/UCSColors";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadOrders,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const enum IconStatuses {
    Processing = "Processing",
    Delivering = "Delivering",
    Received = "Received",
}
const iconMap = new Map();
const Icons = {
    Processing: {
        icon: <StyledIcon src={SettingsWheel} size={24} color="#FF9900" />,
        text: IconStatuses.Processing,
    },
    Delivering: {
        icon: <StyledIcon src={Truck} size={24} color="#40A9FF" />,
        text: IconStatuses.Delivering,
    },
    Received: {
        icon: <StyledIcon src={Checkpoint} size={24} color="#1D8F6A" />,
        text: IconStatuses.Received,
    },
};
iconMap.set(Icons.Processing.text, Icons.Processing.icon);
iconMap.set(Icons.Delivering.text, Icons.Delivering.icon);
iconMap.set(Icons.Received.text, Icons.Received.icon);

export interface OpenOrdersStyles {
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    orderMainContainer: any;
    dataTable: any;
    viewAllButton: any;
    containerName: any;
    topContainerTile: any;
    topHeadingContainer: any;
    topDataContainer: any;
    listNameLink: any;
    wishlistLineItem: any;
}

export const OpenOrdersStyles: OpenOrdersStyles = {
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: { width: 12 },
    orderMainContainer: css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 20px;
    `,
    topHeadingContainer: {
        css: css`
            display: inline-flex;
            flex-direction: row;
            width: 100%;
            margin-bottom: 8px !important;
        `,
    },
    topContainerTile: {
        css: css`
            background: #ffffff;
            padding: 10px;
            padding-left: 20px;
            padding-right: 20px;
            box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
            margin-bottom: 8px;
        `,
    },
    containerName: {
        css: css`
            display: flex;
            flex: 1;
            justify-content: flex-start;
        `,
    },
    viewAllButton: {
        css: css`
            display: flex;
            flex: 1;
            justify-content: flex-end;
        `,
    },
    topDataContainer: {
        css: css``,
    },
    listNameLink: {
        css: css`
            color: ${UCSColors.green00};
            white-space: no-wrap;
        `,
    },
    wishlistLineItem: {
        css: css`
            gap: 10px;
        `,
    },
    dataTable: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            #orderNumId a span {
                color: ${UCSColors.green00} !important;
            }
            ${({ theme }) => {
                const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
                return breakpointMediaQueries(theme, [
                    css`
                        max-width: ${maxWidths[1]}px;
                        #orderNumId {
                            order: 2 !important;
                        }
                        #orderDateId {
                            order: 1 !important;
                        }
                        #totalAmountId {
                            order: 3 !important;
                        }
                        #statusId {
                            order: 4 !important;
                        }
                    `,
                    css`
                        max-width: ${maxWidths[2]}px;
                        #orderNumId {
                            order: 2 !important;
                        }
                        #orderDateId {
                            order: 1 !important;
                        }
                        #totalAmountId {
                            order: 3 !important;
                        }
                        #statusId {
                            order: 4 !important;
                        }
                    `,
                    css`
                        max-width: ${maxWidths[3]}px;
                    `,
                    css`
                        max-width: ${maxWidths[4]}px;
                    `,
                    css`
                        max-width: ${maxWidths[5]}px;
                    `,
                ]);
            }}
        `,
        cell: {
            css: css`
                display: flex;
                flex: 1;
                flex-direction: column;
                align-self: auto;
                justify-self: center;
                color: ${UCSColors.gray03};
            `,
        },
        header: {
            css: css`
                display: flex;
                flex: 1;
                flex-direction: column;
                align-self: auto;
                justify-self: auto;
                text-transform: uppercase;
                font-weight: bold;
                color: ${UCSColors.gray04};
            `,
        },
        headerContainer: {
            css: css`
                display: flex;
                flex-direction: row;
                width: 100%;
                gap: 40px;
            `,
        },
        row: {
            css: css`
                display: flex;
                flex: 1;
                flex-direction: row;
                gap: 40px;
                padding-top: 2px;
                padding-bottom: 2px;
            `,
        },
        previewImages: {
            css: css`
                display: flex;
                flex-direction: row;
                flex: 1;
                gap: 10px;
            `,
        },
        mobilePreviewImages: {
            css: css`
                display: flex;
                flex-direction: row;
                flex: 1;
                gap: 10px;
            `,
        },
        mobileCell: {
            css: css`
                display: flex;
                flex-direction: column;
                white-space: no-wrap;
                color: ${UCSColors.gray03};
            `,
        },
        mobileHeader: {
            css: css`
                display: flex;
                flex-direction: column;
                align-self: center;
                justify-content: flex-start;
                text-transform: uppercase;
                font-weight: bold;
                color: ${UCSColors.gray04};
            `,
        },
        mobileHeaderContainer: {
            css: css`
                display: flex;
                flex-direction: column;
            `,
        },

        mobileRow: {
            css: css`
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-self: start;
            `,
        },
        innerTwoGrid: {
            css: css`
                display: grid;
                grid-template-columns: [first] 86px [line2] auto;
                grid-auto-flow: column;
                align-items: start;
            `,
        },
    },
};

let recentOrdersParameter: GetOrdersApiParameter = {
    page: 1,
    sort: "OrderDate DESC",
};

const styles = OpenOrdersStyles;
class OpenOrders extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    getOrderStatusIcon = (status: string) => {
        return iconMap.get(status);
    };

    render() {
        const { ordersDataView, orderHistoryPageLink, settingsCollection, canViewOrders } = this.props;
        if (!canViewOrders || !ordersDataView.value) {
            return null;
        }
        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;
        if (ordersDataView === null) {
            return;
        }

        const openOrdersDataView = ordersDataView.value.filter(o => o.status.toLowerCase() === "open");

        return (
            <StyledWrapper {...styles.topContainerTile} data-test-selector="myAccountOpenOrdersTable">
                <StyledWrapper {...styles.topHeadingContainer}>
                    <Typography size={28} {...styles.containerName}>
                        {translate("Open Orders")}
                    </Typography>
                    <Link
                        href={orderHistoryUrl}
                        target="_self"
                        icon={{
                            iconProps: {
                                color: `${UCSColors.green00}`,
                                size: 16,
                                src: ChevronRight,
                            },
                        }}
                    >
                        <Typography size={18} {...styles.viewAllButton}>
                            {translate("View all ")}
                        </Typography>
                    </Link>
                </StyledWrapper>
                <StyledWrapper role="presentation" {...styles.dataTable}>
                    <Hidden below="md">
                        <HorizontalRule />
                        <StyledWrapper {...styles.dataTable.headerContainer}>
                            <StyledWrapper id="orderNumId" {...styles.dataTable.header}>
                                <Typography size={13}>Order #</Typography>
                            </StyledWrapper>
                            <StyledWrapper id="orderDateId" {...styles.dataTable.header}>
                                <Typography size={13}>Order Date</Typography>
                            </StyledWrapper>
                            <StyledWrapper id="totalAmountId" {...styles.dataTable.header}>
                                <Typography size={13}>Amount</Typography>
                            </StyledWrapper>
                            <StyledWrapper id="statusId" {...styles.dataTable.header}>
                                <Typography size={13}>Status</Typography>
                            </StyledWrapper>
                            <StyledWrapper id="poId" {...styles.dataTable.header}>
                                <Typography size={13}>PO #</Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        <HorizontalRule />
                    </Hidden>
                    <Hidden above="sm">
                        <HorizontalRule />
                    </Hidden>
                    {openOrdersDataView.length === 0 && (
                        <GridItem {...styles.noOrdersFoundGridItem}>
                            <Typography {...styles.noOrdersFoundText}>
                                {siteMessage("Orders_NoOpenOrders")}
                            </Typography>
                        </GridItem>
                    )}
                    {openOrdersDataView.map((order, index) => {
                        if (order !== null) {
                            const row = {
                                id: order.id,
                                linkOrderNumber: order.webOrderNumber || order.erpOrderNumber,
                                orderNumber: order.webOrderNumber || order.erpOrderNumber,
                                shipTo: `${order.stCompanyName} ${order.stAddress1} ${order.stAddress2} ${order.shipToCity} ${order.shipToState}`,
                                status: order.statusDisplay,
                                po: order.customerPO,
                                orderDate: order.orderDate,
                                total: order.orderGrandTotalDisplay,
                            }
                            return (
                                <StyledWrapper key={row.id}>
                                    <StyledWrapper {...styles.dataTable.row} role="row">
                                        <Hidden below="md" {...styles.dataTable.row}>
                                            <StyledWrapper
                                                id="orderNumId"
                                                className="orderNumberWrapper"
                                                {...styles.dataTable.cell}
                                            >
                                                <OrderDetailPageTypeLink
                                                    title={row.orderNumber}
                                                    orderNumber={row.linkOrderNumber}
                                                />
                                            </StyledWrapper>
                                            <StyledWrapper id="orderDateId" {...styles.dataTable.cell}>
                                                <Typography size={18}>{formatDate(new Date(row.orderDate))}</Typography>
                                            </StyledWrapper>
                                            <StyledWrapper id="totalAmountId" {...styles.dataTable.cell}>
                                                <Typography size={18}>{row.total}</Typography>
                                            </StyledWrapper>
                                            <StyledWrapper id="statusId" {...styles.dataTable.cell}>
                                                <Typography size={18}>{row.status}</Typography>
                                            </StyledWrapper>
                                            <StyledWrapper id="poId" {...styles.dataTable.cell}>
                                                <Typography size={18}>(PO Number)</Typography>
                                            </StyledWrapper>
                                        </Hidden>
                                        <Hidden above="sm" {...styles.dataTable.mobileRow}>
                                            <StyledWrapper id="orderNumId" {...styles.dataTable.mobileCell}>
                                                <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                    <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                        Order #
                                                    </Typography>
                                                    <OrderDetailPageTypeLink
                                                        title={row.orderNumber}
                                                        orderNumber={row.linkOrderNumber}
                                                    />
                                                </StyledWrapper>
                                            </StyledWrapper>
                                            <StyledWrapper id="orderDateId" {...styles.dataTable.mobileCell}>
                                                <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                    <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                        {translate("Order Date")}
                                                    </Typography>
                                                    <StyledWrapper {...styles.dataTable.mobileCell}>
                                                        {formatDate(new Date(row.orderDate))}
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                            <StyledWrapper id="totalAmountId" {...styles.dataTable.mobileCell}>
                                                <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                    <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                        Amount
                                                    </Typography>
                                                    <Typography size={14}>{row.total}</Typography>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                            <StyledWrapper id="statusId" {...styles.dataTable.mobileCell}>
                                                <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                    <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                        Status
                                                    </Typography>
                                                    <Typography size={14}>
                                                        {this.getOrderStatusIcon(row.status)}
                                                        {row.status}
                                                    </Typography>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                            <StyledWrapper id="poId" {...styles.dataTable.mobileCell}>
                                                <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                    <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                        PO #
                                                    </Typography>
                                                    <Typography size={14}>(PO Number)</Typography>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        </Hidden>
                                    </StyledWrapper>
                                    <HorizontalRule />
                                </StyledWrapper>
                            );
                        } 
                        return "";
                    })}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OpenOrders),
    definition: {
        group: "Common",
        icon: "List",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Open Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
